// HeroCarouselItem.tsx
import React, { useMemo } from "react";

// ASSETS
import { ReactComponent as PlaceholderImage } from "assets/svg/PlaceholderImage.svg";

// STYLES
import {
  HeroCarouselCard,
  ImageWrapper,
  Img,
  InfoWrapper,
  SubtitleWrapper,
  TitleWrapper,
} from "./HeroCarouselItemStyled";

// LIBRARIES
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";

// MISC
import useWindowWidth from "hooks/useWindowWidth";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { COLORS, SIZE } from "constants/variables";
import { IProps } from "./HeroCarouselItemModel";
import { useTranslation } from "react-i18next";

// REDUX
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";
import { useSelector } from "react-redux";

// COMPONENTS
import CustomText from "components/Atoms/CustomText/CustomText";
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import { ButtonType } from "components/Atoms/ButtonAtom/ButtonAtomModel";

const HeroCarouselItem: React.FC<IProps> = ({
  title,
  subtitle,
  link,
  imgUrl,
  external,
}) => {
  // ====================== LIBRARY CONSTANTS ======================
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const windowWidth = useWindowWidth();

  // =========================== STATE ============================
  /**
   * We treat the card as “loading” when any of its core props are falsy.
   * This keeps the logic dumb-simple and matches what you did in
   * AuthorisationGuideCardAto
   */
  const isLoading = !title || !subtitle || !imgUrl;

  // ===================== EVENT HANDLERS ========================
  const handleNavigate = () => {
    try {
      if (external) {
        window.open(link, "_blank", "noopener,noreferrer");
      } else {
        navigate(link);
      }
    } catch (error) {
      console.error("Invalid URL", error);
    }
  };

  // ================== MEMO-ISED SKELETON SIZES ================
  /**
   * A small memo to avoid recalculating skeleton dimensions on every render.
   * Sizes shadow the real typography rules from the styled-components file.
   */
  const skeletonDims = useMemo(() => {
    const titleHeight = windowWidth > 1024 ? Number(SIZE.max60) : Number(SIZE.max40);
    const subtitleLine = windowWidth > 1024 ? Number(SIZE.max24) : Number(SIZE.max18);
    const subtitleLinesDesktop = 3.4; // matches -webkit-line-clamp for desktop
    const subtitleLinesMobile = 4.4; // matches clamp for mobile
    const subtitleHeight =
      subtitleLine *
      (windowWidth > 1024 ? subtitleLinesDesktop : subtitleLinesMobile);
    return {
      titleHeight,
      subtitleHeight,
      buttonHeight: 48,
    };
  }, [windowWidth]);

  // =========================== JSX =============================
  return (
    <HeroCarouselCard
      biggerCursor={biggerCursor}
      onClick={!isLoading ? handleNavigate : undefined}
      external={external}
    >
      {isLoading ? (
        /* ---------- SKELETON VARIANT ---------- */
        <>
          <InfoWrapper>
            {/* Title skeleton – two lines, same max height as TitleWrapper */}
            <Skeleton
              count={2}
              width="85%"
              height={skeletonDims.titleHeight}
              style={{ marginBottom: 8 }}
            />
            {/* Subtitle skeleton – one multi-line block */}
            <Skeleton
              width="95%"
              height={skeletonDims.subtitleHeight}
              style={{ marginBottom: 16 }}
            />
            {/* Button skeleton – roughly the size of ArrowButton */}
            <Skeleton
              width={180}
              height={skeletonDims.buttonHeight}
              borderRadius={skeletonDims.buttonHeight / 2}
            />
          </InfoWrapper>

          <ImageWrapper>
            {/* Image skeleton – covers the same surface the Img would */}
            <Skeleton
              width="100%"
              height="100%"
              style={{ transform: "scale(1.01)" }} // tiny overshoot prevents right-edge bleed
            />
          </ImageWrapper>
        </>
      ) : (
        /* ---------- LIVE VARIANT ---------- */
        <>
          <InfoWrapper>
            <TitleWrapper>
              <CustomText
                text={title}
                color={COLORS.AbsoluteWhite}
                type={windowWidth > 1024 ? TextType.H0 : TextType.H1}
              />
            </TitleWrapper>

            <SubtitleWrapper>
              <CustomText
                text={subtitle}
                color={COLORS.Background}
                type={windowWidth > 1024 ? TextType.P0 : TextType.P1}
              />
            </SubtitleWrapper>

            <ButtonAtom
              text={t("components.heroCarouselItem.button")}
              type={ButtonType.ArrowButton}
              rightIcon
              onClick={handleNavigate}
            />
          </InfoWrapper>

          <ImageWrapper>
            {imgUrl ? (
              <Img src={imgUrl} alt={title} loading="lazy" decoding="async" />
            ) : (
              <PlaceholderImage />
            )}
          </ImageWrapper>
        </>
      )}
    </HeroCarouselCard>
  );
};

export default HeroCarouselItem;
